import type { ParentComponent, VoidComponent } from "solid-js";
import { createSignal, Show } from "solid-js";
import Handlebar from "../field/Handlebar";
import { useTranslation } from "../../state/i18n";
import type { WithClassList } from "../../types";
import { IconButtonMenu } from "../listItemMenu/ListItemMenu";
import InlineEditor from "../inlineEditor/InlineEditor";
import { MAX_CHARACTERS_SM } from "../../constants";
import type RuleId from "../../fields/RuleId";
import Text from "../text/Text";
import Draggable from "../../pages/customer/dnd/Draggable";
import { isNil } from "lodash-es";
import DropIndicator from "../../pages/customer/dnd/DropIndicator";

type GenericDraggableProps = {
  label: string;
};

export type DraggableRuleProps = {
  id: RuleId;
  onRename: (value: string) => void;
  onDelete: () => void;
  hasError: boolean;
  onClick: () => void;
  active: boolean;
  index: number;
  isLast: boolean;
} & GenericDraggableProps;

type Variant = "disabled" | "active" | "default";

const Outline: ParentComponent<{ selected: boolean; containerVariant: Variant; hasError: boolean }> = (props) => (
  <div
    class="flex h-9.5 items-center rounded-sm pl-2 text-base-medium text-primary"
    classList={{
      "bg-surface-active-brand": props.selected && props.containerVariant !== "disabled",
      "bg-surface-default": props.containerVariant !== "active" && !props.selected,
      "hover:bg-surface-hover": props.containerVariant === "default" && !props.selected,
      "bg-surface-disabled": props.containerVariant === "disabled",
      "border border-error": props.hasError && props.containerVariant !== "active",
      "relative w-48 cursor-grabbing border border-default shadow-elevation-light-3":
        props.containerVariant === "active",
    }}
  >
    <Handlebar
      classList={{
        "icon-disabled": props.containerVariant === "disabled",
        "cursor-grab": props.containerVariant === "default",
      }}
    />
    {props.children}
  </div>
);

const Label: VoidComponent<WithClassList<{ label: string; disabled: boolean }>> = (props) => (
  <Text
    // oxlint-disable-next-line better-tailwindcss/no-restricted-classes -- one extra pixel to account for border when opening/closing inline editor
    class="grow pl-[9px]"
    classList={{ ...props.classList, "text-disabled": props.disabled }}
    text={props.label}
    truncate
  />
);

const Button: VoidComponent<{
  containerVariant: Variant;
  label: string;
  value: string;
  hasError: boolean;
  onClick: () => void;
  active: boolean;
}> = (props) => (
  <button
    onClick={() => props.onClick()}
    value={props.value}
    class="block w-full rounded-sm text-left focus-visible:outline-default"
  >
    <Outline selected={props.active} containerVariant={props.containerVariant} hasError={props.hasError}>
      <Label
        label={props.label}
        disabled={props.containerVariant === "disabled"}
        classList={{ "pr-8.5": props.containerVariant !== "active", "pr-2.5": props.containerVariant === "active" }}
      />
    </Outline>
  </button>
);

export const DraggableRule: VoidComponent<DraggableRuleProps> = (props) => {
  const { t } = useTranslation();
  const [isEditing, setEditing] = createSignal(false);
  let dragHandle!: HTMLDivElement;

  return (
    <Draggable
      data={{
        type: "RULE",
        id: props.id.toString(),
        index: props.index,
      }}
      dragHandle={dragHandle}
      isLast={props.isLast}
      preview={() => (
        <div class="w-48 bg-surface-default">
          <Button
            value={props.id.toString()}
            containerVariant="active"
            label={props.label}
            hasError={props.hasError}
            onClick={props.onClick}
            active={false}
          />
        </div>
      )}
    >
      {(state) => (
        <div class="py-0.5">
          <Show
            when={!isEditing()}
            fallback={
              <div ref={dragHandle}>
                <Outline selected={props.active} containerVariant="default" hasError={props.hasError}>
                  <div class="overflow-hidden px-1">
                    <InlineEditor
                      label={t(($) => $.RULES_EDIT_NAME_LABEL)}
                      value={props.label}
                      setValue={(value) => {
                        props.onRename(value);
                      }}
                      open={isEditing()}
                      setOpen={(open) => setEditing(open)}
                      maxLength={MAX_CHARACTERS_SM}
                    />
                  </div>
                </Outline>
              </div>
            }
          >
            <div class="relative">
              <Button
                value={props.id.toString()}
                containerVariant={state().type === "is-dragging" ? "disabled" : "default"}
                label={props.label}
                hasError={props.hasError}
                onClick={props.onClick}
                active={props.active}
              />
              <div class="absolute inset-y-0 right-1.5 my-auto flex items-center">
                <IconButtonMenu
                  disabled={(state().type === "is-dragging" ? "disabled" : "default") === "disabled"}
                  items={[
                    [
                      {
                        value: "rename",
                        type: "action",
                        onClick: () => setEditing(true),
                        variant: "text",
                        label: t(($) => $.RENAME_BUTTON),
                      },
                      {
                        value: "delete",
                        type: "action",
                        onClick: props.onDelete,
                        variant: "text",
                        label: t(($) => $.DELETE_BUTTON),
                      },
                    ],
                  ]}
                  aria-label={t(($) => $.ACTIONS_ARIA)}
                />
              </div>
            </div>
          </Show>

          <Show when={state().type === "is-dragging-over" && !isNil(state().closestEdge)}>
            <DropIndicator edge={state().closestEdge!} gap={0} />
          </Show>
        </div>
      )}
    </Draggable>
  );
};
